import React from 'react';
import { Card, CardContent } from './ui/card';
import { Target, Award, Users, TrendingDown, Headphones } from 'lucide-react';
import { mockData } from '../mock';

const About = () => {
  return (
    <section id="about" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        {/* About Content with Image */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              {mockData.about.description}
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              With cutting-edge technology and expert installation, we ensure maximum efficiency and reliability. Our commitment to quality has made us a trusted name in solar energy solutions across India.
            </p>
          </div>
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
              <img 
                src="https://images.unsplash.com/photo-1509391366360-2e959784a276?w=800&h=600&fit=crop" 
                alt="Solar Installation" 
                className="w-full h-[400px] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-gradient-to-r from-lime-500 to-green-600 text-white p-6 rounded-xl shadow-xl">
              <div className="text-2xl font-bold">15+ Years</div>
              <div className="text-sm">Industry Experience</div>
            </div>
          </div>
        </div>

        {/* Mission Statement */}
        <div className="max-w-4xl mx-auto mb-16">
          <Card className="bg-gradient-to-r from-lime-50 to-green-50 border-none shadow-lg">
            <CardContent className="p-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-lime-500 to-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Our Mission</h3>
                  <p className="text-gray-700 leading-relaxed">{mockData.about.mission}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white">
            <CardContent className="p-8 text-center">
              <div className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-lime-600 to-green-600 bg-clip-text text-transparent mb-3">
                {mockData.about.stats[0].number}
              </div>
              <div className="text-gray-600 font-medium">{mockData.about.stats[0].label}</div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white">
            <CardContent className="p-8 text-center">
              <div className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-lime-600 to-green-600 bg-clip-text text-transparent mb-3">
                {mockData.about.stats[1].number}
              </div>
              <div className="text-gray-600 font-medium">{mockData.about.stats[1].label}</div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white">
            <CardContent className="p-8 text-center">
              <div className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-lime-600 to-green-600 bg-clip-text text-transparent mb-3">
                {mockData.about.stats[2].number}
              </div>
              <div className="text-gray-600 font-medium">{mockData.about.stats[2].label}</div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white">
            <CardContent className="p-8 text-center">
              <div className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-lime-600 to-green-600 bg-clip-text text-transparent mb-3">
                {mockData.about.stats[3].number}
              </div>
              <div className="text-gray-600 font-medium">{mockData.about.stats[3].label}</div>
            </CardContent>
          </Card>
        </div>

        {/* Why Choose Us */}
        <div className="mt-20">
          <h3 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Why Choose Varun Solar Energy?
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-none shadow-md hover:shadow-xl transition-all duration-300 group">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Award className="w-7 h-7 text-white" />
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">{mockData.features[0].title}</h4>
                <p className="text-gray-600 text-sm leading-relaxed">{mockData.features[0].description}</p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md hover:shadow-xl transition-all duration-300 group">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Users className="w-7 h-7 text-white" />
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">{mockData.features[1].title}</h4>
                <p className="text-gray-600 text-sm leading-relaxed">{mockData.features[1].description}</p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md hover:shadow-xl transition-all duration-300 group">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <TrendingDown className="w-7 h-7 text-white" />
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">{mockData.features[2].title}</h4>
                <p className="text-gray-600 text-sm leading-relaxed">{mockData.features[2].description}</p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md hover:shadow-xl transition-all duration-300 group">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Headphones className="w-7 h-7 text-white" />
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">{mockData.features[3].title}</h4>
                <p className="text-gray-600 text-sm leading-relaxed">{mockData.features[3].description}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
